import {
  Button,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  Label,
} from "reactstrap";
import { OperationResult } from "urql";
import { CSSProperties, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useAutoSaveMutation } from "./useAutoSaveMutation";
import { useTranslation } from "react-i18next";
import { SaveStatusIndicator } from "./SaveStatusIndicator";

/**
 * Text input component with GraphQL mutation support.
 *
 * This component accepts strictly typed values (string | null) and does not accept undefined.
 * This enforces explicit handling of optional fields at the call site.
 *
 * ## Type Safety
 *
 * The `isRequired` prop changes the signature of `validateAndTransform`:
 * - **isRequired: true** → validateAndTransform returns `string | 'invalid'` (null not allowed)
 * - **isRequired: false** → validateAndTransform returns `string | null | 'invalid'`
 *
 * ## validateAndTransform Usage
 *
 * Return values:
 * - **string**: Valid, transformed value (e.g., trimmed, normalized)
 * - **null**: Empty value is acceptable (only for optional fields)
 * - **'invalid'**: Validation failed, value is not acceptable
 *
 * **Important:** For optional fields, `validateAndTransform` should return `null` for empty strings,
 * not an empty string `""`. Empty strings should be normalized to `null` to clearly indicate "no value".
 *
 * @example
 * // Optional field - empty strings → null
 * <TextInputGraphQlMutate
 *     value={song.author ?? null}
 *     isRequired={false}
 *     validateAndTransform={(value) => {
 *         if (value.trim().length === 0) return null;  // ✓ Return null, not ""
 *         return value.trim();
 *     }}
 * />
 *
 * @example
 * // Required field - empty strings → invalid
 * <TextInputGraphQlMutate
 *     value={song.title}
 *     isRequired={true}
 *     validateAndTransform={(value) => {
 *         if (value.trim().length === 0) return 'invalid';  // ✓ Cannot return null
 *         if (value.length < 3) return 'invalid';
 *         return value.trim();
 *     }}
 * />
 *
 * @example
 * // For optional fields from GraphQL, use nullish coalescing:
 * <TextInputGraphQlMutate value={song.author ?? null} ... />
 *
 * @example
 * // For required fields, pass directly:
 * <TextInputGraphQlMutate value={song.title} isRequired={true} ... />
 */

/** Type helper: Return type of validateAndTransform depends on isRequired */
type ValidationResult<TRequired extends boolean> = TRequired extends true
  ? string | "invalid" // Required fields: string or invalid (no null)
  : string | null | "invalid"; // Optional fields: string, null, or invalid

interface PropsBase<
  TData = unknown,
  TVariables extends Record<string, unknown> = Record<string, unknown>,
> {
  labelValue: string;
  htmlTagIdAndName: string;
  /** Value must be string or null. Use nullish coalescing for optional fields: value={song.author ?? null} */
  value: string | null;
  mutationDocument: TypedDocumentNode<TData, TVariables>;
  buildVariables: (validatedValue: string | null) => TVariables;
  invalidFeedback?: string;
  autoFocus?: boolean;
  isDisabled?: boolean;
  onMutationCompleted?: (r: OperationResult<TData, TVariables>) => void;
  style?: CSSProperties | undefined;
  className?: string | undefined;
}

interface PropsRequired<
  TData = unknown,
  TVariables extends Record<string, unknown> = Record<string, unknown>,
> extends PropsBase<TData, TVariables> {
  isRequired: true;
  /**
   * Custom validation and transformation function for required fields.
   * @param newValue - The current input value
   * @returns string (valid value) or 'invalid' (validation failed). Cannot return null for required fields.
   */
  validateAndTransform?: (newValue: string) => ValidationResult<true>;
}

interface PropsOptional<
  TData = unknown,
  TVariables extends Record<string, unknown> = Record<string, unknown>,
> extends PropsBase<TData, TVariables> {
  isRequired?: false;
  /**
   * Custom validation and transformation function for optional fields.
   * @param newValue - The current input value
   * @returns string (valid value), null (empty/cleared value), or 'invalid' (validation failed)
   */
  validateAndTransform?: (newValue: string) => ValidationResult<false>;
}

type Props<
  TData = unknown,
  TVariables extends Record<string, unknown> = Record<string, unknown>,
> = PropsRequired<TData, TVariables> | PropsOptional<TData, TVariables>;

const defaultValidateAndTransform = (
  newValue: string,
  isRequired: boolean,
): string | null | "invalid" => {
  if (newValue.trim().length === 0) {
    return isRequired ? "invalid" : null;
  }
  return newValue.trim();
};

export function TextInputGraphQlMutate<
  TData = unknown,
  TVariables extends Record<string, unknown> = Record<string, unknown>,
>(props: Props<TData, TVariables>) {
  const { t } = useTranslation();

  const {
    labelValue,
    htmlTagIdAndName,
    value,
    mutationDocument,
    buildVariables,
    validateAndTransform,
    isRequired = false,
    invalidFeedback = t("items.validation.notEmpty"),
    autoFocus = false,
    isDisabled = false,
    onMutationCompleted = () => {},
    style,
    className,
  } = props;

  const internalValidateAndTransform = useMemo(
    () =>
      validateAndTransform ||
      ((v: string) => defaultValidateAndTransform(v, isRequired)),
    [validateAndTransform, isRequired],
  );

  const {
    inputValue,
    setInputValue,
    isValid,
    isFocused,
    inputRef,
    handleFocus,
    handleBlur,
    handleSave,
    handleCancel,
    saveStatus,
  } = useAutoSaveMutation({
    value,
    mutationDocument,
    buildVariables,
    validateAndTransform: internalValidateAndTransform,
    onMutationCompleted,
    hotkey: "enter",
    hotkeyFormTag: "input",
  });

  return (
    <FormGroup>
      <Label for={htmlTagIdAndName} className="d-flex align-items-center">
        {labelValue}
        <SaveStatusIndicator
          htmlTagIdAndName={htmlTagIdAndName}
          status={saveStatus}
        />
      </Label>

      <InputGroup>
        <Input
          innerRef={inputRef}
          id={htmlTagIdAndName}
          name={htmlTagIdAndName}
          style={style}
          className={className}
          placeholder={labelValue}
          value={inputValue}
          onChange={(evt) => setInputValue(evt.currentTarget.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          invalid={!isValid}
          autoFocus={autoFocus}
          disabled={isDisabled}
        />
        {isFocused && (
          <>
            <Button
              color="primary"
              tabIndex={-1}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur event
                handleSave();
              }}
            >
              <FontAwesomeIcon icon={faCheck} />
            </Button>
            <Button
              color="primary"
              tabIndex={-1}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur event
                handleCancel();
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </>
        )}
        <FormFeedback>{invalidFeedback}</FormFeedback>
      </InputGroup>
    </FormGroup>
  );
}
